import { FlatList, Modal, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useColorScheme } from 'nativewind';
import { ErrorMessage } from 'formik';
import { Checkbox, Icon } from '../index';
import svgIcons from '../../assets';
import { cln } from '../../utils/classnames';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { isNative } from '../../utils/responsive';
import { Organization, Topic } from '@/client/interfaces';

type DropdownItem = Organization | Topic;

interface Props {
  containerRef?: React.RefObject<View>;
  inputRef?: React.RefObject<TextInput>;
  dropdownVisible?: boolean;
  onFocus?: () => void;
  onChangeText: (text: string) => void;
  inputValue: string;
  placeholder: string;
  selectedItems?: DropdownItem[];
  selectedItem?: DropdownItem;
  inputOnPress?: () => void;
  filteredContent: DropdownItem[];
  itemOnPress: (item: DropdownItem) => void;
  onClose?: () => void;
  singleSelect?: boolean;
  title?: string;
  required?: boolean;
  name?: string;
  errorMessage?: string;
  zIndex?: string;
  testID?: string;
  mobileDropdownTestId?: string;
}

const DropdownCheckbox: React.FC<Props> = ({
  containerRef: containerRefProp,
  inputRef: inputRefProp,
  dropdownVisible: dropdownVisibleProp,
  onFocus: onFocusProp,
  onChangeText,
  inputValue,
  inputOnPress: inputOnPressProp,
  placeholder,
  selectedItems,
  selectedItem,
  filteredContent,
  itemOnPress,
  onClose,
  singleSelect = false,
  title,
  required,
  name,
  errorMessage,
  zIndex,
  testID,
  mobileDropdownTestId,
}) => {
  const { colorScheme } = useColorScheme();
  const { t } = useTranslation();
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState({ x: 0, y: 0, width: 0 });

  // Internal state - used when external props are not provided
  const {
    visible: internalVisible,
    setVisible: setInternalVisible,
    reference: internalContainerRef,
  } = useOnClickOutside(false);
  const internalInputRef = useRef<TextInput>(null);

  // Use external props if provided, otherwise fall back to internal state
  const containerRef = containerRefProp ?? internalContainerRef;
  const inputRef = inputRefProp ?? internalInputRef;
  const dropdownVisible = dropdownVisibleProp ?? internalVisible;
  const onFocus = onFocusProp ?? (() => setInternalVisible(true));
  const inputOnPress = inputOnPressProp ?? (() => setInternalVisible(!internalVisible));

  const closeDropdown = () => {
    if (onClose) onClose();
    else setInternalVisible(false);
  };

  // Measure input position in window so the Modal dropdown can be placed below it
  useEffect(() => {
    if (dropdownVisible && isNative) {
      containerRef.current?.measureInWindow(
        (x: number, y: number, width: number, height: number) => {
          setDropdownPos({ x, y: y + height, width });
        },
      );
    }
  }, [dropdownVisible]);

  const hasSelection = singleSelect ? !!selectedItem : !!selectedItems?.length;

  const getPlaceholderColor = (isActive: boolean) => {
    if (hasSelection) {
      return isActive
        ? colorScheme === 'dark'
          ? '#101010'
          : '#FAFAFA'
        : colorScheme === 'dark'
          ? '#FAFAFA'
          : '#101010';
    }
    if (!isActive) {
      return colorScheme === 'dark' ? '#888888' : '#101010';
    }
    return colorScheme === 'dark' ? '#101010' : '#888888';
  };

  const getItemLabel = (item: DropdownItem) => {
    const label = 'translationValue' in item ? item.translationValue : item.name;
    return item.isActive === false ? `${label} (${t('common:inactive')})` : label;
  };

  const getDisplayPlaceholder = () => {
    if (singleSelect) {
      return selectedItem ? getItemLabel(selectedItem) : placeholder;
    }
    return `${placeholder}${selectedItems?.length ? ` (${selectedItems.length})` : ''}`;
  };

  const renderItem = ({ item }: { item: DropdownItem }) => (
    <Pressable
      key={item.id}
      onPress={() => {
        setInternalVisible(false);
        itemOnPress(item);
      }}
      onHoverIn={() => setHoveredId(item.id)}
      onHoverOut={() => setHoveredId(null)}
      className={cln(
        'px-3 py-3',
        hoveredId === item.id
          ? 'bg-neutral-200 dark:bg-neutral-700'
          : 'bg-neutral-100 dark:bg-neutral-900',
      )}
    >
      {singleSelect ? (
        <Text className="text-input text-neutral-950 dark:text-neutral-50">
          {getItemLabel(item)}
        </Text>
      ) : (
        <View pointerEvents="none">
          <Checkbox
            checked={selectedItems?.some((s) => s.id === item.id)}
            label={getItemLabel(item)}
          />
        </View>
      )}
    </Pressable>
  );

  // Web: inline absolutely-positioned list inside the input container
  const webDropdownList = (
    <FlatList
      testID={testID ? `${testID}-list` : undefined}
      className="absolute w-full max-h-52 z-50 top-12"
      keyboardShouldPersistTaps="handled"
      nestedScrollEnabled
      data={filteredContent}
      renderItem={renderItem}
    />
  );

  return (
    <View className={cln('relative', !isNative && 'flex-1', zIndex ? zIndex : 'z-10')}>
      {title && (
        <View className="flex-row mb-1">
          <Text className="text-input text-neutral-950 dark:text-neutral-50 font-[sans-600]">
            {title}
          </Text>
          {required && <Text className="text-input text-danger-500 font-[sans-400]"> *</Text>}
        </View>
      )}
      <View
        ref={containerRef}
        className={cln('flex-row items-center relative z-80', !isNative && 'flex-1')}
        style={isNative ? { height: 48 } : undefined}
      >
        <TextInput
          ref={inputRef}
          testID={testID}
          placeholderTextColor={getPlaceholderColor(dropdownVisible)}
          className={cln(
            dropdownVisible
              ? 'text-neutral-50 bg-neutral-950 dark:text-neutral-950 dark:bg-neutral-50'
              : 'text-neutral-950 bg-neutral-50 dark:text-neutral-50 dark:bg-neutral-950',
            'h-12 border-[1px] flex-1 px-3 border-neutral-900 dark:border-neutral-200',
          )}
          onFocus={onFocus}
          onChangeText={onChangeText}
          value={inputValue}
          keyboardType="ascii-capable"
          placeholder={getDisplayPlaceholder()}
        />
        <View className="absolute right-[8px] z-20">
          <Pressable testID={mobileDropdownTestId} onPress={inputOnPress} className="py-2 px-2">
            <Icon
              icon={dropdownVisible ? svgIcons.chevronUpIcon : svgIcons.chevronDownIcon}
              mobileSize={{ width: 24, height: 24 }}
              webSize={{ width: 24, height: 24 }}
              inverted={dropdownVisible}
            />
          </Pressable>
        </View>
        {dropdownVisible && !isNative && webDropdownList}
      </View>
      {/* Native: Modal so the FlatList is in its own render layer with no gesture interception */}
      {isNative && (
        <Modal
          visible={dropdownVisible}
          transparent
          animationType="none"
          onRequestClose={closeDropdown}
        >
          <Pressable style={StyleSheet.absoluteFillObject} onPress={closeDropdown} />
          {dropdownPos.width > 0 && (
            <View
              style={{
                position: 'absolute',
                top: dropdownPos.y,
                left: dropdownPos.x,
                width: dropdownPos.width,
                maxHeight: 208,
              }}
            >
              <FlatList
                keyboardShouldPersistTaps="handled"
                data={filteredContent}
                renderItem={renderItem}
              />
            </View>
          )}
        </Modal>
      )}
      {!dropdownVisible && (
        <>
          {name && (
            <ErrorMessage name={name}>
              {(message) => (
                <Text className="text-notificationMessage text-danger-500 font-[sans-600] mt-1">
                  {message}
                </Text>
              )}
            </ErrorMessage>
          )}
          {errorMessage && (
            <Text className="text-notificationMessage text-danger-500 font-[sans-600] mt-1">
              {errorMessage}
            </Text>
          )}
        </>
      )}
    </View>
  );
};

export default DropdownCheckbox;
